import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const honeypot = (e.currentTarget.elements.namedItem('website_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setStatus('success');
      setEmail('');
      return;
    }
    if (!email.trim()) return;
    setStatus('submitting');
    try {
      const formData = new URLSearchParams();
      formData.append('email', email.trim());
      await fetch('https://readdy.ai/api/form/d92ev0r8srkha17lojsg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formData.toString(),
      });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('success');
      setEmail('');
    }
  };

  return (
    <footer className="bg-foreground-900 mx-4 md:mx-6 mb-4 md:mb-6 rounded-2xl overflow-hidden">
      <div className="px-8 md:px-12 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12">
          <div className="md:col-span-5">
            <h3 className="text-background-50 font-heading text-3xl md:text-4xl font-light">
              Stay Updated
            </h3>
            <p className="text-background-50/50 text-sm mt-3 leading-relaxed">
              Get notified when we add new spreadsheet guides, category updates, and platform comparisons.
            </p>
            <form
              id="newsletter-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-end gap-3"
            >
              <div className="flex-1 relative">
                <input
                  type="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                  className="w-full bg-transparent border-b border-background-50/30 text-background-50 text-sm py-2.5 px-0 outline-none focus:border-background-50 transition-colors placeholder:text-background-50/30"
                />
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="honeypot-field"
                />
              </div>
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="inline-flex items-center gap-2 bg-background-50 text-foreground-900 px-6 py-2.5 rounded-full text-sm font-medium hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50"
              >
                {status === 'success' ? 'Subscribed!' : 'Subscribe'}
                {status !== 'success' && (
                  <span className="w-5 h-5 flex items-center justify-center bg-foreground-900 rounded-full">
                    <i className="ri-arrow-right-line text-background-50 text-[10px]"></i>
                  </span>
                )}
              </button>
            </form>
          </div>

          <div className="md:col-span-3">
            <h4 className="text-background-50/40 text-xs tracking-widest uppercase font-medium mb-5">
              Navigate
            </h4>
            <nav className="flex flex-col gap-3.5">
              <Link to="/what-is-gtbuy-spreadsheet" className="text-background-50/70 text-sm hover:text-background-50 transition-colors border-b border-background-50/20 pb-1 w-fit">
                What is GTBuy Spreadsheet
              </Link>
              <Link to="/spreadsheet-comparison" className="text-background-50/70 text-sm hover:text-background-50 transition-colors border-b border-background-50/20 pb-1 w-fit">
                Spreadsheet Comparison
              </Link>
              <Link to="/best-gtbuy-spreadsheet-sources" className="text-background-50/70 text-sm hover:text-background-50 transition-colors border-b border-background-50/20 pb-1 w-fit">
                Best Sources
              </Link>
              <Link to="/spreadsheet-safety" className="text-background-50/70 text-sm hover:text-background-50 transition-colors border-b border-background-50/20 pb-1 w-fit">
                Safety Guide
              </Link>
              <a href="#categories" className="text-background-50/70 text-sm hover:text-background-50 transition-colors border-b border-background-50/20 pb-1 w-fit">
                Categories
              </a>
              <Link to="/spreadsheet-beginners-guide" className="text-background-50/70 text-sm hover:text-background-50 transition-colors border-b border-background-50/20 pb-1 w-fit">
                Shopping Guide
              </Link>
            </nav>
          </div>

          <div className="md:col-span-4">
            <h4 className="text-background-50/40 text-xs tracking-widest uppercase font-medium mb-5">
              Contact
            </h4>
            <div className="flex flex-col gap-2.5 text-background-50/70 text-sm">
              <a href="mailto:hello@gtbuyspreadsheet.living" className="hover:text-background-50 transition-colors border-b border-background-50/20 pb-1 w-fit">
                hello@gtbuyspreadsheet.living
              </a>
            </div>

            <h4 className="text-background-50/40 text-xs tracking-widest uppercase font-medium mt-8 mb-4">
              Legal
            </h4>
            <p className="text-background-50/50 text-sm leading-relaxed">
              This is an independent information portal.<br />
              We are not affiliated with GTBuy, Kakobuy, or Superbuy.
            </p>
          </div>
        </div>

        <div className="border-t border-background-50/10 mt-12 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-5">
            <a href="#" className="text-background-50/50 text-sm hover:text-background-50 transition-colors border-b border-background-50/20 pb-0.5">
              Privacy Policy
            </a>
            <a href="#" className="text-background-50/50 text-sm hover:text-background-50 transition-colors border-b border-background-50/20 pb-0.5">
              Terms
            </a>
          </div>
          <p className="text-background-50/30 text-xs">
            &copy; 2026 GTBuy Spreadsheet Hub. All rights reserved.
          </p>
          <div className="flex items-center gap-5">
            <a href="#" className="text-background-50/50 text-sm hover:text-background-50 transition-colors border-b border-background-50/20 pb-0.5">
              Twitter
            </a>
            <a href="#" className="text-background-50/50 text-sm hover:text-background-50 transition-colors border-b border-background-50/20 pb-0.5">
              Facebook
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}